import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, map } from 'rxjs';
import { V3ImageCropDialogComponent } from '../../components-v3/image-crop-dialog/v3-image-crop-dialog.component';
import { ImageCropperData, ImageCropResult } from '../../models/images/image-crop.model';
import {
  ConfirmationPopupComponent,
  ConfirmationPopupData,
  ConfirmationPopupResult,
} from '../../styleguide/components/confirmation-popup/confirmation-popup.component';
import { LanguageCodeHelper } from '../language-code-helper';

/**
 * - `default`: Standard confirmation with a primary blue confirm button and equal-width actions.
 * - `destructive`: For irreversible actions (e.g. delete, archive). Confirm button is rendered
 * in alert red and takes up 70% of the actions row to draw attention to the risk.
 */
export type ConfirmDialogStyle = 'default' | 'destructive';

export interface V3ConfirmDialogConfig {
  message?: string;
  title?: string | HTMLElement;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonType?: 'primary' | 'secondary' | 'secondary-neutral';
  cancelButtonType?: 'primary' | 'secondary' | 'secondary-neutral';
  style?: ConfirmDialogStyle;
}

@Injectable({
  providedIn: 'root',
})
export class V3DialogService {
  private dialog = inject(MatDialog);
  private transloco = inject(TranslocoService);

  showConfirmDialog(config: V3ConfirmDialogConfig): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '440px',
      maxWidth: '95vw',
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-v3-dialog',
      backdropClass: 'ptl-v3-dialog-backdrop-default',
      autoFocus: false,
      data: {
        title: config.title || this.transloco.translate('translations.global.button.confirm'),
        message: config.message || '',
        cancelButtonText: config.cancelButtonText || this.transloco.translate('translations.global.button.cancel'),
        confirmButtonText: config.confirmButtonText || this.transloco.translate('translations.global.button.confirm'),
        cancelButtonType: config.cancelButtonType || 'secondary-neutral',
        confirmButtonType: config.confirmButtonType || (config.style === 'destructive' ? 'primary-alert' : 'primary'),
        style: config.style,
      } as ConfirmationPopupData,
    });

    return firstValueFrom(dialogRef.afterClosed().pipe(map((result: ConfirmationPopupResult | undefined) => result?.confirmed ?? false)));
  }

  showImageCropDialog(data: ImageCropperData): Promise<ImageCropResult | undefined> {
    const dialogRef = this.dialog.open<V3ImageCropDialogComponent, ImageCropperData, ImageCropResult>(V3ImageCropDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-v3-dialog',
      backdropClass: 'ptl-v3-dialog-backdrop-default',
      autoFocus: false,
      data,
    });

    return firstValueFrom(dialogRef.afterClosed());
  }
}
